import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import TopNav from '../../components/TopNav'
import ContextBar from '../../components/ContextBar'
import { getCustomer, getAnalysisHistory } from '../../api'
import './AnalysisResults.css'

export default function AnalysisResults() {
  const { customerId, analysisId } = useParams()
  const [customer, setCustomer] = useState(null)
  const [analysis, setAnalysis] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('variances')
  const [panelTab, setPanelTab] = useState('provenance')
  const [severityFilter, setSeverityFilter] = useState('all')
  const [expandedRow, setExpandedRow] = useState(null)

  useEffect(() => {
    async function load() {
      try {
        const [ctx, hist] = await Promise.allSettled([
          getCustomer(customerId),
          getAnalysisHistory(customerId),
        ])
        if (ctx.status === 'fulfilled') setCustomer(ctx.value)
        if (hist.status === 'fulfilled') {
          const analyses = hist.value?.analyses || hist.value?.history || (Array.isArray(hist.value) ? hist.value : [])
          const found = analyses.find(a => a.id === analysisId || a.analysis_id === analysisId)
          setAnalysis(found || analyses[0] || null)
        }
      } catch (err) {
        console.error('Failed to load:', err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [customerId, analysisId])

  const companyName = customer?.company_name || customerId

  // Mock findings data for display (backend would provide this)
  const findings = analysis?.findings || [
    { severity: 'critical', record: 'CA Employees (247)', field: 'SDI Withholding', source: 'CA-SDI: Enabled', target: '—', diff: 'Not configured', diffType: 'missing', explanation: 'California requires State Disability Insurance (SDI) withholding for all eligible employees. Without this in UKG Pro, Acme will fail to withhold SDI from 247 California employees\' paychecks.', provenance: 'tax_config WHERE state=\'CA\' × employees WHERE work_state=\'CA\'', confidence: '98%' },
    { severity: 'critical', record: 'NYC Employees (83)', field: 'Local Tax', source: 'LC-NYC: 3.876%', target: '—', diff: 'Not configured', diffType: 'missing', explanation: '83 employees in New York City jurisdiction have no local withholding configured in UKG Pro target.', provenance: 'payroll_legacy × tax_config WHERE jurisdiction=\'NYC\'', confidence: '96%' },
    { severity: 'critical', record: 'Active Garnishments (14)', field: 'Garnishment Service', source: 'ADP Garnish: Active', target: 'Not subscribed', diff: 'Service missing', diffType: 'missing', explanation: 'UKG Pro garnishment service is not subscribed. 14 employees have active garnishments.', provenance: 'Systems tab → Vendor Services', confidence: '99%' },
    { severity: 'critical', record: 'W-2 Processing', field: 'W-2 Management', source: 'ADP W-2: Active', target: 'Not subscribed', diff: 'Service missing', diffType: 'missing', explanation: 'UKG Pro W-2 Management service is not subscribed — must be purchased before year-end.', provenance: 'Systems tab → Vendor Services', confidence: '99%' },
    { severity: 'warning', record: 'Earning Code: BONUS-Q', field: 'Earning Code', source: 'BONUS-Q', target: '—', diff: 'No UKG equiv', diffType: 'missing', explanation: 'Custom ADP bonus code with no direct UKG equivalent.', provenance: 'mapping_engine: ADP_earning → UKG_earning', confidence: '87%' },
    { severity: 'warning', record: '312 Employees', field: 'Pay Frequency', source: 'Semi-monthly', target: 'Biweekly', diff: 'Mismatch', diffType: 'mismatch', explanation: 'ADP shows semi-monthly pay for 312 employees in the legacy payroll group. UKG is configured for biweekly only.', provenance: 'payroll_legacy.pay_frequency × employees', confidence: '94%' },
    { severity: 'warning', record: 'Earning Code: COMM-T1', field: 'Earning Code', source: 'COMM-T1', target: '—', diff: 'No UKG equiv', diffType: 'missing', explanation: 'Custom commission code with no direct UKG equivalent.', provenance: 'mapping_engine: ADP_earning → UKG_earning', confidence: '85%' },
    { severity: 'warning', record: 'Earning Code: SHIFT-D3', field: 'Earning Code', source: 'SHIFT-D3', target: 'SHIFT_DIFF (partial)', diff: 'Partial match', diffType: 'mismatch', explanation: 'Partial match — UKG has a generic shift differential but ADP code is more specific.', provenance: 'mapping_engine: ADP_earning → UKG_earning', confidence: '72%' },
    { severity: 'compatible', record: 'Federal Tax Config', field: 'FIT/FICA/FUTA', source: 'Standard', target: 'Standard', diff: 'Match ✓', diffType: 'match', explanation: 'All federal withholding setups match between systems.', provenance: 'tax_config WHERE level=\'federal\'', confidence: '99%' },
    { severity: 'compatible', record: 'Medicare Config', field: 'Medicare/Add\'l', source: 'Standard + 0.9%', target: 'Standard + 0.9%', diff: 'Match ✓', diffType: 'match', explanation: 'Medicare configuration matches.', provenance: 'tax_config WHERE type=\'medicare\'', confidence: '99%' },
  ]

  const patterns = [
    { severity: 'critical', title: 'Systematic tax jurisdiction gap for employees in no-income-tax states', detail: '3 states (NH, TX, WY) have employees but no tax configuration in either ADP or UKG. While these states don\'t have income tax, they may have local withholding requirements (e.g., Houston MTA).', affected: '186 employees across 3 states', confidence: '94%' },
    { title: 'Custom earning codes cluster around bonus and commission structures', detail: '7 of the 9 unmapped earning codes are bonus or commission variants (BONUS-Q, BONUS-A, COMM-T1, COMM-T2, COMM-SPOT, BONUS-REF, BONUS-SIGN). This suggests Acme has a complex incentive comp structure.', affected: '9 earning codes', confidence: '87%' },
    { title: 'Pay frequency mismatch isolated to legacy acquisition group', detail: 'All 312 employees with the semi-monthly/biweekly mismatch belong to the "Legacy Acme East" pay group — likely from an acquisition.', affected: '312 employees in 1 pay group', confidence: '91%' },
  ]

  const recommendations = [
    { text: 'Configure CA-SDI withholding in UKG Pro for all 247 California employees before running parallel payroll.', effort: '~4 hours · Critical · Tax Setup' },
    { text: 'Add NYC local tax jurisdiction and assign to 83 NYC employees. Verify rate matches current ADP config (3.876%).', effort: '~3 hours · Critical · Tax Setup' },
    { text: 'Subscribe to UKG Garnishment Service and migrate 14 active garnishment orders. Coordinate with UKG account rep.', effort: '~8 hours · Critical · Vendor · Requires external coordination' },
    { text: 'Subscribe to UKG W-2 Management before year-end. Current ADP contract likely covers W-2s through Dec 31.', effort: '~4 hours · Critical · Vendor' },
    { text: 'Schedule earning code mapping workshop with Acme\'s compensation team. Bring the 9 unmapped codes and AI-suggested matches.', effort: '~6 hours · Warning · Mapping · Requires client' },
    { text: 'Resolve pay frequency for Legacy Acme East group (312 employees). Decision needed: convert to biweekly, or configure UKG for dual pay frequencies.', effort: '~6 hours · Warning · Requires client decision' },
    { text: 'Investigate local withholding requirements for TX, NH, WY employees.', effort: '~3 hours · Advisory · Compliance' },
    { text: 'Run parallel payroll test after completing items 1-4. Compare gross-to-net calculations between ADP and UKG.', effort: '~12 hours · Validation · Gate: requires items 1-4 complete' },
  ]

  const provenanceSteps = [
    { title: 'Step 1: ADP Config Pull', detail: 'payroll_legacy → 47 earning codes, 23 deductions, 12 jurisdictions\nRows scanned: 2,928 · Duration: 4.2s' },
    { title: 'Step 2: Semantic Mapping', detail: 'ADP earning codes → UKG earning catalog\nAuto-matched: 38/47 (81%) · Manual review: 9\nHub version: v2.3 · Model: exact + fuzzy + semantic' },
    { title: 'Step 3: Tax Jurisdiction Compare', detail: 'tax_config × employees × compliance_rules\nJurisdictions compared: 12 · Variances found: 3\nConfidence threshold: 90%' },
    { title: 'Step 4: Deduction Gap Analysis', detail: 'ADP deductions → UKG deduction catalog\nGaps identified: 7 · Partial matches: 2' },
    { title: 'Step 5: Compliance Check', detail: 'FLSA, state wage/hour, tax filing requirements\nRules checked: 142 · Violations: 0 · Warnings: 3' },
    { title: 'Step 6: Synthesis', detail: 'All findings aggregated and ranked by severity\nTotal findings: 44 · Critical: 4 · Warning: 7 · Compatible: 31 · Blocked: 2' },
  ]

  const termMappings = [
    { their: '"cost center"', ours: 'Department' },
    { their: '"home company"', ours: 'Legal Entity' },
    { their: '"pay class"', ours: 'Pay Group' },
    { their: '"worker type"', ours: 'Employment Type' },
    { their: '"position title"', ours: 'Job Title' },
    { their: '"org level 1"', ours: 'Division' },
    { their: '"employee class"', ours: 'FLSA Status' },
    { their: '"file number"', ours: 'Employee ID' },
  ]

  const severityCounts = {
    all: findings.length,
    critical: findings.filter(f => f.severity === 'critical').length,
    warning: findings.filter(f => f.severity === 'warning').length,
    compatible: findings.filter(f => f.severity === 'compatible').length,
  }

  const filtered = severityFilter === 'all'
    ? findings
    : findings.filter(f => f.severity === severityFilter)

  const matchRate = findings.length > 0
    ? Math.round((findings.filter(f => f.severity === 'compatible').length / findings.length) * 100)
    : 0

  const breadcrumbs = [
    { label: 'Customers', href: '/customers' },
    { label: companyName, href: `/customers/${customerId}` },
    { label: 'Analysis', href: `/customers/${customerId}/analysis` },
    { label: analysis?.title || 'Results' },
  ]
  const navActions = (
    <>
      <button className="nav-btn">Re-run</button>
      <Link to={`/customers/${customerId}/workplan/new`} className="nav-btn">Generate Workplan</Link>
      <Link to={`/customers/${customerId}/exports`} className="nav-btn primary">Export</Link>
    </>
  )

  if (loading) {
    return (
      <>
        <TopNav breadcrumbs={breadcrumbs} actions={navActions} />
        <div className="ar-loading"><div className="ar-spinner" /><p>Loading analysis results…</p></div>
      </>
    )
  }

  return (
    <>
      <TopNav breadcrumbs={breadcrumbs} actions={navActions} />

      {/* Context Bar */}
      <div className="ar-context-bar">
        <div className="ar-context-left">
          <div className="ar-context-avatar">{companyName.substring(0, 2).toUpperCase()}</div>
          <div className="ar-context-info">
            <div className="ar-context-company">{companyName}</div>
            <div className="ar-context-detail">{customer?.industry || 'Technology'} · {customer?.employee_count || '—'} employees · {customer?.primary_system || 'UKG Pro'}</div>
          </div>
        </div>
        <div className="ar-context-right">
          <span className="ar-badge mode">{analysis?.mode?.replace(/_/g, ' ') || 'Gap Analysis'}</span>
          <span className="ar-badge date">{analysis?.created_at ? new Date(analysis.created_at).toLocaleDateString() : 'Feb 5, 2026'}</span>
          <span className="ar-badge time">{analysis?.duration || '41 min'} · {analysis?.step_count || 6} steps</span>
        </div>
      </div>

      <div className="ar-workspace">
        {/* LEFT: Results Panel */}
        <div className="ar-results-panel">

          {/* Summary Strip */}
          <div className="ar-summary-strip">
            <div className="ar-summary-stat">
              <div className="ar-summary-icon match">✓</div>
              <div>
                <div className="ar-summary-value">{matchRate}%</div>
                <div className="ar-summary-label">Match Rate</div>
              </div>
            </div>
            <div className="ar-summary-stat">
              <div className="ar-summary-icon critical">!</div>
              <div>
                <div className="ar-summary-value">{severityCounts.critical}</div>
                <div className="ar-summary-label">Critical</div>
              </div>
            </div>
            <div className="ar-summary-stat">
              <div className="ar-summary-icon warning">⚠</div>
              <div>
                <div className="ar-summary-value">{severityCounts.warning}</div>
                <div className="ar-summary-label">Warnings</div>
              </div>
            </div>
            <div className="ar-summary-stat">
              <div className="ar-summary-icon info">ℹ</div>
              <div>
                <div className="ar-summary-value">{findings.length}</div>
                <div className="ar-summary-label">Total Findings</div>
              </div>
            </div>
          </div>

          {/* Results Tabs */}
          <div className="ar-results-tabs">
            <button className={`ar-results-tab ${activeTab === 'variances' ? 'active' : ''}`} onClick={() => setActiveTab('variances')}>
              Variances <span className="ar-tab-count">{findings.length}</span>
            </button>
            <button className={`ar-results-tab ${activeTab === 'patterns' ? 'active' : ''}`} onClick={() => setActiveTab('patterns')}>
              Patterns <span className="ar-tab-count">{patterns.length}</span>
            </button>
            <button className={`ar-results-tab ${activeTab === 'recommendations' ? 'active' : ''}`} onClick={() => setActiveTab('recommendations')}>
              Recommendations <span className="ar-tab-count">{recommendations.length}</span>
            </button>
          </div>

          {/* Tab Content */}
          <div className="ar-results-content">

            {/* Variances Tab */}
            {activeTab === 'variances' && (
              <div className="ar-variance-wrap">
                <div className="ar-variance-toolbar">
                  <div className="ar-toolbar-left">
                    <button className={`ar-filter-pill ${severityFilter === 'all' ? 'active' : ''}`} onClick={() => setSeverityFilter('all')}>All {severityCounts.all}</button>
                    <button className={`ar-filter-pill critical ${severityFilter === 'critical' ? 'active' : ''}`} onClick={() => setSeverityFilter('critical')}>Critical {severityCounts.critical}</button>
                    <button className={`ar-filter-pill warning ${severityFilter === 'warning' ? 'active' : ''}`} onClick={() => setSeverityFilter('warning')}>Warning {severityCounts.warning}</button>
                    <button className={`ar-filter-pill ${severityFilter === 'compatible' ? 'active' : ''}`} onClick={() => setSeverityFilter('compatible')}>Compatible {severityCounts.compatible}</button>
                  </div>
                  <span className="ar-variance-count">Showing {filtered.length} of {findings.length}</span>
                </div>
                <table className="ar-variance-table">
                  <thead>
                    <tr>
                      <th style={{ width: 50 }}>Sev</th>
                      <th>Record</th>
                      <th>Field</th>
                      <th>Source (ADP)</th>
                      <th>Target (UKG)</th>
                      <th>Difference</th>
                      <th style={{ width: 50 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((f, i) => (
                      <>
                        <tr key={`row-${i}`}>
                          <td><span className={`ar-severity-dot ${f.severity}`}>{f.severity === 'critical' ? 'Critical' : f.severity === 'warning' ? 'Warning' : 'Info'}</span></td>
                          <td className="ar-record-cell">{f.record}</td>
                          <td className="ar-field-cell">{f.field}</td>
                          <td><span className="ar-val-source">{f.source}</span></td>
                          <td><span className="ar-val-target">{f.target}</span></td>
                          <td><span className={`ar-val-diff ${f.diffType}`}>{f.diff}</span></td>
                          <td>
                            {f.explanation && (
                              <button className="ar-explain-btn" onClick={() => setExpandedRow(expandedRow === i ? null : i)}>
                                {expandedRow === i ? 'Hide' : 'Explain'}
                              </button>
                            )}
                          </td>
                        </tr>
                        {expandedRow === i && (
                          <tr key={`exp-${i}`} className="ar-expanded-row">
                            <td colSpan={7}>
                              <div className="ar-expanded-content">
                                <strong>Why this matters:</strong> {f.explanation}
                                <div className="ar-expanded-provenance">
                                  Source: {f.provenance}<br />
                                  Confidence: {f.confidence}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Patterns Tab */}
            {activeTab === 'patterns' && (
              <div className="ar-patterns-section">
                <div className="ar-section-title">Detected Patterns <span className="ar-count">{patterns.length} patterns</span></div>
                {patterns.map((p, i) => (
                  <div key={i} className={`ar-pattern-card ${p.severity || ''}`}>
                    <div className="ar-pattern-title">{p.title}</div>
                    <div className="ar-pattern-detail">{p.detail}</div>
                    <div className="ar-pattern-affected">Affects: {p.affected} · Pattern confidence: {p.confidence}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Recommendations Tab */}
            {activeTab === 'recommendations' && (
              <div className="ar-recommendations-section">
                <div className="ar-section-title">Recommended Actions <span className="ar-count">{recommendations.length} actions</span></div>
                {recommendations.map((r, i) => (
                  <div key={i} className="ar-rec-item">
                    <div className="ar-rec-number">{i + 1}</div>
                    <div className="ar-rec-content">
                      <div className="ar-rec-text">{r.text}</div>
                      <div className="ar-rec-effort">{r.effort}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: Context Panel */}
        <div className="ar-context-panel">
          <div className="ar-panel-tabs">
            <button className={`ar-panel-tab ${panelTab === 'provenance' ? 'active' : ''}`} onClick={() => setPanelTab('provenance')}>Provenance</button>
            <button className={`ar-panel-tab ${panelTab === 'sources' ? 'active' : ''}`} onClick={() => setPanelTab('sources')}>Sources</button>
            <button className={`ar-panel-tab ${panelTab === 'sql' ? 'active' : ''}`} onClick={() => setPanelTab('sql')}>SQL</button>
            <button className={`ar-panel-tab ${panelTab === 'terms' ? 'active' : ''}`} onClick={() => setPanelTab('terms')}>Terms</button>
          </div>

          <div className="ar-panel-content">
            {/* Provenance Tab */}
            {panelTab === 'provenance' && (
              <div className="ar-provenance-section">
                <div className="ar-provenance-label">Analysis Chain</div>
                {provenanceSteps.map((s, i) => (
                  <div key={i} className="ar-provenance-item">
                    <div className="ar-provenance-item-title">{s.title}</div>
                    <div className="ar-provenance-item-detail">{s.detail}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Sources Tab */}
            {panelTab === 'sources' && (
              <div className="ar-provenance-section">
                <div className="ar-provenance-label">Data Sources Used</div>
                <div className="ar-provenance-item">
                  <div className="ar-provenance-item-title">payroll_legacy (ADP)</div>
                  <div className="ar-provenance-item-detail">2,928 rows · Source: ADP Workforce Now API · Last sync: 3d ago</div>
                </div>
                <div className="ar-provenance-item">
                  <div className="ar-provenance-item-title">employees (UKG Pro)</div>
                  <div className="ar-provenance-item-detail">2,847 rows · Source: UKG Pro REST API · Last sync: 2h ago</div>
                </div>
                <div className="ar-provenance-item">
                  <div className="ar-provenance-item-title">tax_config</div>
                  <div className="ar-provenance-item-detail">5,694 rows · Source: File upload (tax_config_2025.xlsx) · Uploaded: 3d ago</div>
                </div>
                <div className="ar-provenance-item">
                  <div className="ar-provenance-item-title">compensation_details (UKG Pro)</div>
                  <div className="ar-provenance-item-detail">2,847 rows · Source: UKG Pro REST API · Last sync: 2h ago</div>
                </div>
                <div className="ar-provenance-item">
                  <div className="ar-provenance-item-title">Compliance Rules</div>
                  <div className="ar-provenance-item-detail">142 federal rules · 89 CA rules · 67 NY rules · Knowledge base v2.3</div>
                </div>
              </div>
            )}

            {/* SQL Tab */}
            {panelTab === 'sql' && (
              <div className="ar-provenance-section">
                <div className="ar-provenance-label">Generated SQL</div>
                <div className="ar-sql-block">
                  <div className="ar-sql-label">Tax Jurisdiction Compare</div>
                  <pre><code>{`SELECT e.work_state, 
       COUNT(*) AS emp_count,
       t.state_code AS tax_state,
       CASE WHEN t.state_code IS NULL 
            THEN 'MISSING' ELSE 'OK' 
       END AS status
FROM employees e
LEFT JOIN tax_config t 
  ON e.work_state = t.state_code
GROUP BY e.work_state, t.state_code
ORDER BY status DESC, emp_count DESC`}</code></pre>
                </div>
                <div className="ar-sql-block">
                  <div className="ar-sql-label">Earning Code Mapping</div>
                  <pre><code>{`SELECT a.earning_code,
       a.description AS adp_name,
       u.earning_code AS ukg_equiv,
       CASE WHEN u.earning_code IS NULL
            THEN 'GAP' ELSE 'MAPPED'
       END AS status
FROM adp_earning_codes a
LEFT JOIN ukg_earning_catalog u
  ON a.semantic_key = u.semantic_key
ORDER BY status, a.earning_code`}</code></pre>
                </div>
                <div className="ar-sql-block">
                  <div className="ar-sql-label">Deduction Gap Analysis</div>
                  <pre><code>{`SELECT a.deduction_code, 
       a.description AS adp_name,
       u.deduction_type AS ukg_equiv,
       CASE WHEN u.deduction_type IS NULL
            THEN 'GAP' ELSE 'MAPPED' 
       END AS status
FROM adp_deductions a
LEFT JOIN ukg_deduction_catalog u
  ON a.semantic_key = u.semantic_key`}</code></pre>
                </div>
              </div>
            )}

            {/* Terms Tab */}
            {panelTab === 'terms' && (
              <>
                <div className="ar-provenance-section">
                  <div className="ar-provenance-label">Term Mappings Applied</div>
                  {termMappings.map((t, i) => (
                    <div key={i} className="ar-term-row">
                      <span className="ar-term-their">{t.their}</span>
                      <span className="ar-term-arrow">→</span>
                      <span className="ar-term-ours">{t.ours}</span>
                    </div>
                  ))}
                </div>
                <div className="ar-provenance-section">
                  <div className="ar-provenance-label">Customer Terminology Source</div>
                  <div className="ar-provenance-item">
                    <div className="ar-provenance-item-detail">
                      {termMappings.length} term mappings loaded from {companyName} context<br />
                      Applied to: SQL generation, column resolution, display labels
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Panel Actions */}
          <div className="ar-panel-actions">
            <Link to={`/customers/${customerId}/workplan/new`} className="nav-btn" style={{ flex: 1, textAlign: 'center' }}>Generate Workplan</Link>
            <Link to={`/customers/${customerId}/exports`} className="nav-btn primary" style={{ flex: 1, textAlign: 'center' }}>Export Report</Link>
          </div>
        </div>
      </div>
    </>
  )
}
